import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  #renderingNext = false;
  #renderingPrevious = false;

  #pageGeneration = 0;

  /** @type {MutationObserver | undefined} */
  #filterObserver;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.updateProductOrder();
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#pageGeneration++;
    this.#filterObserver?.disconnect();
    this.#filterObserver = undefined;
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateProductOrder();
  }

  updateProductOrder() {
    const { grid } = this.refs;
    if (!grid || !this.hasAttribute('data-stock-last')) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const loadedPages = new Set(cards.map((card) => Number(card.getAttribute('data-page'))));
    const lastPage = Number(grid.dataset.lastPage);

    cards.sort((a, b) => {
      const availability = Number(a.getAttribute('data-available') === 'false') -
        Number(b.getAttribute('data-available') === 'false');
      return availability || Number(a.getAttribute('data-page')) - Number(b.getAttribute('data-page')) ||
        Number(a.getAttribute('data-sort-index')) - Number(b.getAttribute('data-sort-index'));
    });

    for (const [index, card] of cards.entries()) {
      if (grid.children[index] !== card) grid.insertBefore(card, grid.children[index] ?? null);
    }

    grid.toggleAttribute('data-stock-order-complete', loadedPages.size === lastPage && loadedPages.has(1));
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      if (this.hasAttribute('data-stock-last')) this.infinityScrollObserver.unobserve(viewMorePrevious);
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      if (this.hasAttribute('data-stock-last')) this.infinityScrollObserver.unobserve(viewMoreNext);
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const generation = this.#pageGeneration;
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    if (generation !== this.#pageGeneration) return;
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const generation = this.#pageGeneration;
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    if (!this.isConnected || generation !== this.#pageGeneration) return;
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#renderingNext) return;
    this.#renderingNext = true;

    try {
      await this.#appendNextPage();
    } finally {
      this.#renderingNext = false;
    }
  }

  async #appendNextPage() {
    const generation = this.#pageGeneration;
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    if (!this.isConnected || generation !== this.#pageGeneration || grid !== this.refs.grid ||
      grid.querySelector(`[data-page="${nextPage.page}"]`)) return;

    grid.append(...nextPageItemElements);

    this.updateProductOrder();

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
      if (this.hasAttribute('data-stock-last')) this.#observeViewMore();
    });
  }

  async #renderPreviousPage() {
    if (this.#renderingPrevious) return;
    this.#renderingPrevious = true;

    try {
      await this.#prependPreviousPage();
    } finally {
      this.#renderingPrevious = false;
    }
  }

  async #prependPreviousPage() {
    const generation = this.#pageGeneration;
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    if (!this.isConnected || generation !== this.#pageGeneration || grid !== this.refs.grid ||
      grid.querySelector(`[data-page="${previousPage.page}"]`)) return;

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.updateProductOrder();

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
      if (this.hasAttribute('data-stock-last')) this.#observeViewMore();
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    if (this.#filterObserver) return;
    const { grid } = this.refs;
    const isPrevious = type === 'previous';

    if (!grid) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');

    if (!cards.length) return;

    const loadedPages = Array.from(cards, (card) => Number(card.getAttribute('data-page')));
    const currentCardPage = isPrevious ? Math.min(...loadedPages) : Math.max(...loadedPages);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#pageGeneration++;
    this.pages.clear();
    this.infinityScrollObserver?.disconnect();
    this.#filterObserver?.disconnect();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Sorting can replace the results without changing the page count.
    // Refresh refs and ordering after the grid morph, including an empty grid.
    this.#filterObserver = new MutationObserver((records) => {
      const gridChanged = records.some(({ target, addedNodes, removedNodes }) =>
        target instanceof Element && (target.matches('[ref="grid"], [ref="cards[]"]') ||
          [...addedNodes, ...removedNodes].some((node) => node instanceof Element &&
            (node.matches('[ref="grid"]') || node.querySelector('[ref="grid"]'))))
      );
      if (!gridChanged) return;

      this.#filterObserver?.disconnect();
      this.#filterObserver = undefined;
      if (!this.isConnected) return;

      this.updatedCallback();
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    });
    this.#filterObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-last-page', 'data-stock-order-complete', 'data-product-id', 'data-sort-index', 'data-available'],
      childList: true,
      subtree: true,
    });
  };
}
